// src/components/forms/MedicalCenterForm.jsx
import React, { useEffect, useMemo, useState } from 'react';
import FormField from './FormField';
import useForm from '../../hooks/useForm';
import { TIME_ZONES } from '../../constants/formConstants';
import { medicalCenterService } from '../../services/medicalCenterService';

const MedicalCenterForm = ({
  medicalCenter = null,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const initialData = {
    name: '',
    address: '',
    time_zone: 'America/New_York',
    preferred_language: ''
  };

  const { formData, errors, handleChange, validate, reset, setFormData } = useForm(initialData);
  const [languages, setLanguages] = useState([]);
  const [availablePrograms, setAvailablePrograms] = useState([]);
  const [selectedProgramIds, setSelectedProgramIds] = useState([]);
  const [programsError, setProgramsError] = useState(null);

  const lockedProgramIds = useMemo(
    () => new Set((medicalCenter?.programs || []).map((program) => program.id)),
    [medicalCenter]
  );

  useEffect(() => {
    const loadFormOptions = async () => {
      try {
        const languagesData = await medicalCenterService.getLanguages();
        setLanguages(languagesData || []);
      } catch (err) {
        console.error('Failed to load languages:', err);
      }

      try {
        const programsData = await medicalCenterService.getAvailablePrograms();
        setAvailablePrograms(Array.isArray(programsData) ? programsData : []);
        setProgramsError(null);
      } catch (err) {
        console.error('Failed to load available programs:', err);
        setAvailablePrograms([]);
        setProgramsError(err.message || 'Failed to load programs');
      }
    };
    loadFormOptions();
  }, []);

  useEffect(() => {
    if (medicalCenter) {
      setFormData({
        name: medicalCenter.name || '',
        address: medicalCenter.address || '',
        time_zone: medicalCenter.time_zone || 'America/New_York',
        preferred_language: medicalCenter.preferred_language || ''
      });
      setSelectedProgramIds((medicalCenter.programs || []).map((program) => program.id));
    } else {
      setSelectedProgramIds([]);
    }
  }, [medicalCenter, setFormData]);

  const handleProgramToggle = (programId, checked) => {
    if (lockedProgramIds.has(programId)) return;

    setSelectedProgramIds((prev) => {
      if (checked) {
        return prev.includes(programId) ? prev : [...prev, programId];
      }
      return prev.filter((id) => id !== programId);
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = { ...formData };
    const newProgramIds = selectedProgramIds.filter(
      (id) => !lockedProgramIds.has(id)
    );

    if (newProgramIds.length > 0) {
      payload.medical_center_programs_attributes = newProgramIds.map((programId) => ({
        program_id: programId,
      }));
    }

    onSubmit(payload);
  };

  const handleCancel = () => {
    reset();
    setSelectedProgramIds([]);
    onCancel();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormField
        label="Medical Center Name"
        name="name"
        value={formData.name}
        onChange={handleChange}
        error={errors.name}
        required
        placeholder="Enter medical center name"
      />

      <div>
        <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
          Address
        </label>
        <textarea
          id="address"
          name="address"
          value={formData.address}
          onChange={handleChange}
          placeholder="Enter medical center address"
          rows={3}
          className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.address ? 'border-red-300' : 'border-gray-300'
          }`}
        />
        {errors.address && (
          <p className="mt-1 text-sm text-red-600">{errors.address}</p>
        )}
      </div>

      <FormField
        label="Time Zone"
        name="time_zone"
        value={formData.time_zone}
        onChange={handleChange}
        error={errors.time_zone}
        required
        options={TIME_ZONES.map((tz) => ({
          value: tz,
          label: tz.replace('_', ' '),
        }))}
      />

      <FormField
        label="Default Language"
        name="preferred_language"
        value={formData.preferred_language}
        onChange={handleChange}
        error={errors.preferred_language}
        required
        options={languages.map((lang) => ({
          value: lang.code,
          label: lang.name,
        }))}
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Programs</label>
        <p className="text-sm text-gray-500 mb-2">
          Optional. Select programs this medical center can use.
          {medicalCenter
            ? ' Already attached programs cannot be removed here.'
            : ''}
        </p>
        {programsError ? (
          <p className="text-sm text-red-600">
            Could not load programs: {programsError}
          </p>
        ) : availablePrograms.length === 0 ? (
          <p className="text-sm text-gray-500">No programs available.</p>
        ) : (
          <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3">
            {availablePrograms.map((program) => {
              const isLocked = lockedProgramIds.has(program.id);
              const isChecked = selectedProgramIds.includes(program.id);

              return (
                <div key={program.id} className="flex items-center">
                  <input
                    id={`program-${program.id}`}
                    type="checkbox"
                    checked={isChecked}
                    disabled={isLocked || loading}
                    onChange={(e) => handleProgramToggle(program.id, e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-60"
                  />
                  <label
                    htmlFor={`program-${program.id}`}
                    className={`ml-2 text-sm font-medium ${
                      isLocked ? 'text-gray-500' : 'text-gray-700'
                    }`}
                  >
                    {program.name}
                    {isLocked ? ' (attached)' : ''}
                  </label>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={handleCancel}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Saving...' : medicalCenter ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
};

export default MedicalCenterForm;
